<template>
  <div class="embed-chooser">
    <div class="embed-chooser-modes" role="radiogroup" aria-label="What to embed">
      <label class="embed-chooser-mode">
        <input type="radio" value="app" v-model="mode" />
        <span>
          <span class="embed-chooser-name">Whole app</span>
          <span class="embed-chooser-hint">The menu and every page.</span>
        </span>
      </label>
      <label class="embed-chooser-mode">
        <input type="radio" value="page" v-model="mode" />
        <span>
          <span class="embed-chooser-name">One page</span>
          <span class="embed-chooser-hint">That page, without the app menu. Links and redirects to other pages still open.</span>
        </span>
      </label>
      <label class="embed-chooser-mode">
        <input type="radio" value="view" v-model="mode" />
        <span>
          <span class="embed-chooser-name">One view</span>
          <span class="embed-chooser-hint">A form or another view, without the rest of the page.</span>
        </span>
      </label>
    </div>

    <p v-if="loadError" class="embed-chooser-status">{{ loadError }}</p>
    <p v-else-if="loading" class="embed-chooser-status">Loading pages…</p>

    <label v-if="mode !== 'app'" class="embed-chooser-field" for="embed-page">
      Page
      <b-form-select id="embed-page" v-model="pageUniqueTitle" :options="pageOptions" :disabled="!pageOptions.length" />
    </label>
    <label v-if="mode === 'view'" class="embed-chooser-field" for="embed-view">
      View
      <b-form-select id="embed-view" v-model="viewUniqueTitle" :options="viewOptions" :disabled="!viewOptions.length" />
    </label>

    <EmbedSnippet :src="src" :title="snippetTitle" :note="note" />
  </div>
</template>

<script>
import axios from 'axios'
import { liveAppHref } from '../../../shared/app-host.js'
import { embedPathTail, embedTargets } from '../../../shared/embed.js'
import EmbedSnippet from './EmbedSnippet.vue'

export default {
  name: 'EmbedChooser',
  components: { EmbedSnippet },
  props: {
    application: { type: String, default: '' },
    pages: { type: Array, default: null },
    startMode: { type: String, default: 'app' },
    startPage: { type: String, default: '' },
    startView: { type: String, default: '' }
  },
  data () {
    return {
      mode: this.startMode || 'app',
      pageUniqueTitle: this.startPage || '',
      viewUniqueTitle: this.startView || '',
      loadedPages: null,
      loading: false,
      loadError: ''
    }
  },
  computed: {
    pageList () {
      return this.pages || this.loadedPages || []
    },
    choices () {
      return embedTargets(this.pageList)
    },
    pageOptions () {
      return this.choices.pages.map((page) => ({
        value: page.uniqueTitle,
        text: page.title === page.uniqueTitle ? page.title : `${page.title} (${page.uniqueTitle})`
      }))
    },
    viewsOnPage () {
      return this.choices.views.filter((view) => view.pageUniqueTitle === this.pageUniqueTitle)
    },
    viewOptions () {
      return this.viewsOnPage.map((view) => ({
        value: view.uniqueTitle,
        text: `${view.title} · ${view.type}`
      }))
    },
    selectedPage () {
      return this.choices.pages.find((page) => page.uniqueTitle === this.pageUniqueTitle) || null
    },
    selectedView () {
      return this.viewsOnPage.find((view) => view.uniqueTitle === this.viewUniqueTitle) || null
    },
    src () {
      if (!this.application || this.loading || this.loadError) return ''
      const tail = embedPathTail({
        mode: this.mode,
        pageUniqueTitle: this.pageUniqueTitle,
        viewUniqueTitle: this.viewUniqueTitle
      })
      if (!tail) return ''
      return liveAppHref(window.location, window.__SUPERTURBO_API__, this.application, tail)
    },
    snippetTitle () {
      if (this.mode === 'view' && this.selectedView) return this.selectedView.title
      if (this.mode === 'page' && this.selectedPage) return this.selectedPage.title
      return this.application || 'App'
    },
    note () {
      if (this.mode === 'app') {
        return 'Paste this iframe on another site to show the whole app, including the menu. Visitors sign in the same way they do on the live app.'
      }
      if (this.mode === 'page') {
        if (!this.pageOptions.length) return 'Add a page, then choose it here.'
        if (this.selectedPage && this.selectedPage.requiresLogin) {
          return 'This page requires login. The iframe shows the sign-in gate until the visitor signs in with the same login as the app.'
        }
        return 'Paste this iframe on another site to show this page without the app menu. A link or a redirect to another page in this app still opens that page. A view that requires login stays closed until the visitor signs in.'
      }
      if (!this.pageOptions.length) return 'Add a page with a view, then choose it here.'
      if (!this.viewOptions.length) return 'This page has no view to embed. Choose another page, or add a view.'
      if (!this.selectedView) return 'Choose the view to embed.'
      if (this.selectedView.requiresLogin || (this.selectedPage && this.selectedPage.requiresLogin)) {
        return 'This view requires login. The iframe asks the visitor to sign in, then shows the view. The same row rules apply.'
      }
      return 'Paste this iframe on another site to show only this view. A link or a redirect to another page opens that page without the app menu.'
    }
  },
  watch: {
    pageList: {
      immediate: true,
      handler () {
        this.ensureSelection()
      }
    },
    mode () {
      this.ensureSelection()
    },
    pageUniqueTitle () {
      this.ensureSelection()
    }
  },
  mounted () {
    if (this.pages == null) this.loadPages()
  },
  methods: {
    async loadPages () {
      if (!this.application) return
      this.loading = true
      this.loadError = ''
      try {
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/pages/`, {
          headers: { 'application-unique-title': this.application }
        })
        this.loadedPages = Array.isArray(res.data) ? res.data : []
      } catch (err) {
        this.loadError = 'The pages could not be loaded.'
        this.loadedPages = []
      } finally {
        this.loading = false
      }
    },
    ensureSelection () {
      if (this.mode === 'app') return
      const pages = this.choices.pages
      if (!pages.some((page) => page.uniqueTitle === this.pageUniqueTitle)) {
        const preferred = pages.find((page) => page.uniqueTitle === this.startPage)
        this.pageUniqueTitle = (preferred || pages[0] || {}).uniqueTitle || ''
      }
      if (this.mode !== 'view') return
      const views = this.viewsOnPage
      if (!views.some((view) => view.uniqueTitle === this.viewUniqueTitle)) {
        const preferred = views.find((view) => view.uniqueTitle === this.startView)
        this.viewUniqueTitle = (preferred || views[0] || {}).uniqueTitle || ''
      }
    }
  }
}
</script>

<style scoped>
.embed-chooser-modes {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 0.9rem;
}

.embed-chooser-mode {
  display: flex;
  gap: 0.65rem;
  align-items: flex-start;
  margin: 0;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--bs-border-color, #dee2e6);
  border-radius: 0.4rem;
  cursor: pointer;
}

.embed-chooser-mode:has(input:checked) {
  border-color: var(--bs-primary, #0d6efd);
}

.embed-chooser-name,
.embed-chooser-hint {
  display: block;
}

.embed-chooser-name {
  font-weight: 600;
}

.embed-chooser-hint,
.embed-chooser-status {
  color: #b0b0ae;
}

.embed-chooser-field {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.embed-chooser-field select,
.embed-chooser-field :deep(select) {
  display: block;
  width: 100%;
  margin-top: 0.3rem;
  font-weight: 400;
}

.embed-chooser-status {
  margin: 0 0 0.75rem;
}
</style>
